import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./PaymentHistory.css";
import { websitePlanPaymentService } from "service/websitePlanPaymentService";

const DEFAULT_FROM = "2024-01-01";
const DEFAULT_TO = new Date().toISOString().slice(0, 10);

// Format currency helper to render clean Indian Rupees
const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value || "-";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PaymentHistory = () => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fromDate, setFromDate] = useState(DEFAULT_FROM);
  const [toDate, setToDate] = useState(DEFAULT_TO);
  const [appliedRange, setAppliedRange] = useState({ from: DEFAULT_FROM, to: DEFAULT_TO });

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await websitePlanPaymentService.getHistory();
      const apiList = Array.isArray(res?.data) ? res.data : [];
      
      // Combine with locally saved purchased service logs
      let localLogs = [];
      try {
        const saved = localStorage.getItem("mokshiq_paid_service_history");
        if (saved) localLogs = JSON.parse(saved);
      } catch (e) {}

      setHistoryData([...localLogs, ...apiList]);
    } catch (err) {
      setError(err.message || "Failed to load payment history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const parsePurchaseDate = (dateStr) => {
    if (!dateStr) return new Date();
    const str = String(dateStr).trim();

    // Standard ISO or YYYY-MM-DD
    if (str.includes("-") && str.split("-")[0].length === 4) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) return d;
    }

    // DD-MM-YYYY or DD/MM/YYYY
    const delimiter = str.includes("-") ? "-" : str.includes("/") ? "/" : null;
    if (delimiter) {
      const [datePart] = str.split(" ");
      const parts = datePart.split(delimiter).map(Number);
      if (parts.length === 3) {
        let [day, month, year] = parts;
        if (year < 100) year += 2000;
        const d = new Date(year, month - 1, day);
        if (!isNaN(d.getTime())) return d;
      }
    }

    const fallback = new Date(str);
    return isNaN(fallback.getTime()) ? new Date() : fallback;
  };

  const filteredData = useMemo(() => {
    const from = new Date(appliedRange.from);
    const to = new Date(appliedRange.to);
    to.setHours(23, 59, 59, 999);

    return historyData.filter((item) => {
      const itemDate = parsePurchaseDate(item.purchaseDate || item.purchase_date || item.date);
      return itemDate >= from && itemDate <= to;
    });
  }, [historyData, appliedRange]);

  // Calculate dynamic KPIs based on filtered data
  const kpis = useMemo(() => {
    let totalSpentNum = 0;
    let successCount = 0;

    filteredData.forEach((item) => {
      if (item.status === "Success") {
        successCount++;
        const amt = parseFloat(item.post) || parseFloat(item.price) || 0;
        totalSpentNum += amt;
      }
    });

    const successRateVal = filteredData.length > 0
      ? Math.round((successCount / filteredData.length) * 100)
      : 0;

    return {
      totalSpent: totalSpentNum,
      totalTransactions: filteredData.length,
      successRate: successRateVal,
    };
  }, [filteredData]);

  const handleSearch = () => {
    setAppliedRange({ from: fromDate, to: toDate });
  };

  return (
    <div className="opay-history-container mt-1">
      {/* Top Header Filter Bar with Breadcrumbs */}
      <div className="opay-history-header-bar mb-3">
        <div className="opay-history-left">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="opay-history-home-icon me-1"
          >
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <ul className="opay-history-breadcrumb">
            <li className="opay-history-breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <li className="opay-history-separator">/</li>
            <li className="opay-history-breadcrumb-item">Website Plan</li>
            <li className="opay-history-separator">/</li>
            <li className="opay-history-breadcrumb-item active">Payment History</li>
          </ul>
          {loading && (
            <div className="spinner-border spinner-border-sm text-primary ms-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
        </div>

        <div className="opay-history-right">
          <input
            type="date"
            className="opay-history-date-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <span className="opay-history-arrow">⇄</span>
          <input
            type="date"
            className="opay-history-date-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <button className="opay-history-search-btn" onClick={handleSearch}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            Search
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Dynamic KPI Dashboard Cards */}
      <div className="opay-history-kpi-grid">
        <div className="opay-history-kpi-card spent">
          <div className="opay-history-kpi-info">
            <span className="opay-history-kpi-label">Total Spent</span>
            <span className="opay-history-kpi-val">{formatCurrency(kpis.totalSpent)}</span>
          </div>
          <div className="opay-history-kpi-icon-wrapper">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
        </div>

        <div className="opay-history-kpi-card transactions">
          <div className="opay-history-kpi-info">
            <span className="opay-history-kpi-label">Transactions</span>
            <span className="opay-history-kpi-val">{kpis.totalTransactions}</span>
          </div>
          <div className="opay-history-kpi-icon-wrapper">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
        </div>

        <div className="opay-history-kpi-card rate">
          <div className="opay-history-kpi-info">
            <span className="opay-history-kpi-label">Success Rate</span>
            <span className="opay-history-kpi-val">{kpis.successRate}%</span>
          </div>
          <div className="opay-history-kpi-icon-wrapper">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
        </div>
      </div>

      {/* Main Table Card with Heading inside */}
      <div className="opay-history-table-card">
        <span className="opay-table-title">Payment History</span>
        <div className="table-responsive">
          <table className="table opay-history-table">
            <thead>
              <tr>
                <th className="text-center" style={{ width: "40px" }}>
                  <input type="checkbox" className="custom-table-checkbox" />
                </th>
                <th className="text-start">Plan Name</th>
                <th>Month</th>
                <th>No Of Days</th>
                <th>Payment Type</th>
                <th>Sleep Days</th>
                <th>Sleep Penalty</th>
                <th>Price</th>
                <th>Gst</th>
                <th>Pre(₹)</th>
                <th>Post(₹)</th>
                <th>Purchase Date</th>
                <th>Active Date</th>
                <th>Renewal Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">
                      <input type="checkbox" className="custom-table-checkbox" />
                    </td>
                    <td className="text-start">
                      <span className="opay-badge opay-badge-purple">{item.planName}</span>
                    </td>
                    <td>{item.month}</td>
                    <td>{item.noOfDays}</td>
                    <td>
                      <span className="opay-badge opay-badge-blue">{item.paymentType}</span>
                    </td>
                    <td>{item.sleepDays}</td>
                    <td className="opay-amount">{formatCurrency(item.sleepPenalty)}</td>
                    <td className="opay-amount">{formatCurrency(item.price)}</td>
                    <td className="opay-amount">{formatCurrency(item.gst)}</td>
                    <td className="opay-amount">{formatCurrency(item.pre)}</td>
                    <td className="opay-amount is-post">{formatCurrency(item.post)}</td>
                    <td className="text-nowrap">{item.purchaseDate}</td>
                    <td className="text-nowrap">{item.activeDate}</td>
                    <td className="text-nowrap">{item.renewalDate}</td>
                    <td>
                      <span
                        className={`opay-badge ${
                          item.status === "Success" ? "opay-badge-success" : "opay-badge-pending"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={15} className="opay-history-no-data text-center py-4 text-muted">
                    {loading ? "Loading…" : "No records found for the selected date range."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;